import React, { useState } from 'react';
import api from '@/api';
import { toast } from 'sonner';
import { Sparkle, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import MarkdownMessage from './MarkdownMessage';

// Splits an AI response into ordered chunks of either {kind:'text', content} or {kind:'action', spec}
const parseActionBlocks = (raw) => {
  if (!raw || typeof raw !== 'string') return [{ kind: 'text', content: raw || '' }];
  const re = /```action\s*([\s\S]*?)```/g;
  const out = [];
  let lastIdx = 0;
  let m;
  while ((m = re.exec(raw)) !== null) {
    if (m.index > lastIdx) out.push({ kind: 'text', content: raw.slice(lastIdx, m.index) });
    try {
      const spec = JSON.parse(m[1].trim());
      out.push({ kind: 'action', spec });
    } catch {
      out.push({ kind: 'text', content: m[0] }); // fall back to raw
    }
    lastIdx = re.lastIndex;
  }
  if (lastIdx < raw.length) out.push({ kind: 'text', content: raw.slice(lastIdx) });
  return out;
};

const ActionButton = ({ spec, onSuccess }) => {
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const handleRun = async () => {
    setBusy(true);
    try {
      if (spec.type === 'bulk-move-applications') {
        const ids = spec.application_ids || [];
        if (!spec.program_id || !ids.length || !spec.target_status) throw new Error('Missing fields');
        await api.post(
          `/vc/programs/${spec.program_id}/applications/bulk-move`,
          { application_ids: ids, status: spec.target_status }
        );
        setDone(true);
        toast.success(`Moved ${ids.length} application${ids.length === 1 ? '' : 's'} to ${spec.target_status}`);
        window.dispatchEvent(new CustomEvent('ai-action-applied', { detail: { type: spec.type, program_id: spec.program_id, application_ids: ids, target_status: spec.target_status } }));
        onSuccess?.();
      } else if (spec.type === 'apply-to-program') {
        // (Iter 48) Founder-facing: deep-link them into the program apply page so they
        // can confirm answers / upload deck before submission. Better UX than a silent
        // one-click which could submit stale answers.
        if (!spec.program_id) throw new Error('Missing program_id');
        window.location.href = `/startup/programs/${spec.program_id}/apply`;
        setDone(true);
      } else if (spec.type === 'request-intro') {
        // (Iter 48) AI-recommended intro — fire the same endpoint the marketplace uses.
        if (!spec.vendor_id) throw new Error('Missing vendor_id');
        const kind = spec.vendor_kind || 'service';
        if (kind === 'agency') {
          await api.post('/intro-calls', { agency_id: spec.vendor_id, note: spec.note || `Suggested by AI coach: ${spec.label || 'introduction request'}` });
        } else {
          await api.post('/vendor-intros', { vendor_id: spec.vendor_id, note: spec.note || `Suggested by AI coach: ${spec.label || 'introduction request'}` });
        }
        setDone(true);
        toast.success('Intro request sent');
        onSuccess?.();
      } else {
        toast.error(`Unknown action: ${spec.type}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Action failed');
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="my-2 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-success/10 border border-success/30 text-success text-xs font-medium" data-testid="ai-action-done">
        <CheckCircle size={14} weight="fill" /> {spec.type === 'request-intro' ? 'Intro requested' : spec.type === 'apply-to-program' ? 'Opening application…' : 'Applied'}
      </div>
    );
  }

  const count = spec.application_ids?.length || 0;
  const label = spec.label
    || (spec.type === 'bulk-move-applications' ? `Move ${count} to ${spec.target_status}`
        : spec.type === 'apply-to-program'     ? 'Apply to program'
        : spec.type === 'request-intro'        ? 'Request intro'
        : 'Run action');

  const toneStyles = {
    accepted:   'bg-success/10 hover:bg-success/20 border-success/40 text-success',
    rejected:   'bg-danger/10 hover:bg-danger/20 border-danger/40 text-danger',
    waitlisted: 'bg-warning/10 hover:bg-warning/20 border-warning/40 text-warning',
    received:   'bg-info/10 hover:bg-info/20 border-info/40 text-info',
  };
  // For the founder-facing actions, color by kind: programs → indigo, intros → emerald/amber/violet.
  let cls = toneStyles[spec.target_status] || 'bg-primary/10 hover:bg-primary/20 border-primary/40 text-primary';
  if (spec.type === 'apply-to-program') cls = 'bg-indigo-500/10 hover:bg-indigo-500/20 border-indigo-500/40 text-indigo-500';
  else if (spec.type === 'request-intro') {
    const k = spec.vendor_kind;
    cls = k === 'agency' ? 'bg-violet-500/10 hover:bg-violet-500/20 border-violet-500/40 text-violet-500'
        : k === 'credit' ? 'bg-amber-500/10 hover:bg-amber-500/20 border-amber-500/40 text-amber-500'
        :                  'bg-emerald-500/10 hover:bg-emerald-500/20 border-emerald-500/40 text-emerald-500';
  }

  return (
    <button onClick={handleRun} disabled={busy}
      className={`my-2 inline-flex items-center gap-2 px-3.5 py-2 rounded-lg ${cls} border text-xs font-semibold transition-all disabled:opacity-50`}
      data-testid={`ai-action-btn-${spec.type}`}>
      <Sparkle size={12} weight="fill" /> {label} <ArrowRight size={12} weight="bold" />
    </button>
  );
};

const AIActionRenderer = ({ content, onActionSuccess }) => {
  const chunks = parseActionBlocks(content);
  return (
    <div>
      {chunks.map((c, i) =>
        c.kind === 'text'
          ? <MarkdownMessage key={i} content={c.content} />
          : <ActionButton key={i} spec={c.spec} onSuccess={onActionSuccess} />
      )}
    </div>
  );
};

export default AIActionRenderer;
